import { Pipe, PipeTransform } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";

/**
 * Sanitizes HTML while preserving inline styles (color, background-color, etc.).
 *
 * Angular's default [innerHTML] and DomSanitizer.sanitize(SecurityContext.HTML)
 * both strip inline styles entirely. This pipe manually removes dangerous
 * elements and attributes (scripts, event handlers, javascript: URIs) while
 * keeping style attributes intact, then bypasses Angular's sanitizer on the
 * cleaned result.
 */
@Pipe({
    name: 'safeStyleHtml',
    standalone: false
})
export class SafeStyleHtmlPipe implements PipeTransform {
    private static readonly DANGEROUS_ELEMENTS = new Set([
        'script', 'iframe', 'object', 'embed', 'form', 'applet',
        'base', 'link', 'meta', 'style', 'svg', 'math',
    ]);

    private static readonly DANGEROUS_ATTR_PATTERN = /^on/i;
    private static readonly DANGEROUS_URL_PATTERN = /^\s*javascript\s*:/i;
    private static readonly SAFE_URL_PATTERN = /^\s*(https?:|data:image\/|mailto:|tel:)/i;

    constructor(private sanitizer: DomSanitizer) {}

    transform(value: string): SafeHtml {
        if (!value) return '';
        const cleaned = this.sanitize(value);
        return this.sanitizer.bypassSecurityTrustHtml(cleaned);
    }

    private sanitize(html: string): string {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        this.cleanNode(doc.body);
        return doc.body.innerHTML;
    }

    private cleanNode(node: Node): void {
        const toRemove: Node[] = [];

        node.childNodes.forEach(child => {
            if (child.nodeType === Node.ELEMENT_NODE) {
                const el = child as Element;
                const tag = el.tagName.toLowerCase();

                // Remove dangerous elements entirely
                if (SafeStyleHtmlPipe.DANGEROUS_ELEMENTS.has(tag)) {
                    toRemove.push(child);
                    return;
                }

                // Remove dangerous attributes (event handlers, javascript: URIs)
                const attrsToRemove: string[] = [];
                for (let i = 0; i < el.attributes.length; i++) {
                    const attr = el.attributes[i];
                    if (SafeStyleHtmlPipe.DANGEROUS_ATTR_PATTERN.test(attr.name)) {
                        attrsToRemove.push(attr.name);
                    } else if (attr.name === 'action') {
                        attrsToRemove.push(attr.name);
                    } else if (
                        (attr.name === 'href' || attr.name === 'src') &&
                        attr.value.trim() !== '' &&
                        !SafeStyleHtmlPipe.SAFE_URL_PATTERN.test(attr.value)
                    ) {
                        attrsToRemove.push(attr.name);
                    }
                }
                attrsToRemove.forEach(name => el.removeAttribute(name));

                // Recurse into children
                this.cleanNode(el);
            }
        });

        toRemove.forEach(child => node.removeChild(child));
    }
}
